/**
 * Error / event reporter indirection for the web app.
 *
 * Routes events through Sentry when `VITE_SENTRY_DSN` is set, otherwise
 * just logs to the console. Sentry calls are safe no-ops if init was
 * skipped, but we still guard with `dsn` to avoid bundling overhead.
 *
 * Mirrors MobileApp/src/reporting/reporter.ts so the two surfaces share
 * the same mental model (and we get coherent dashboards downstream).
 */

import * as Sentry from '@sentry/react';

const sentryEnabled = Boolean(import.meta.env.VITE_SENTRY_DSN);

export interface ReportContext {
  tags?: Record<string, string | number | boolean | undefined>;
  extra?: Record<string, unknown>;
  correlationId?: string;
}

export type Severity = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

let currentUserId: string | undefined;

export function setReporterUser(userId: string | undefined) {
  currentUserId = userId;
  if (sentryEnabled) {
    Sentry.setUser(userId ? { id: userId } : null);
  }
}

export function reportError(error: unknown, ctx: ReportContext = {}) {
  const payload = describe(error);
  // eslint-disable-next-line no-console
  console.error('[report]', payload.name, payload.message, {
    ...ctx,
    userId: currentUserId,
  });
  if (sentryEnabled) {
    Sentry.captureException(error, {
      tags: ctx.tags as Record<string, string>,
      extra: { ...ctx.extra, correlationId: ctx.correlationId },
    });
  }
}

export function reportMessage(
  message: string,
  severity: Severity = 'info',
  ctx: ReportContext = {},
) {
  // eslint-disable-next-line no-console
  console.log('[report]', severity.toUpperCase(), message, {
    ...ctx,
    userId: currentUserId,
  });
  if (sentryEnabled) {
    Sentry.captureMessage(message, {
      level: severity,
      tags: ctx.tags as Record<string, string>,
      extra: ctx.extra,
    });
  }
}

function describe(error: unknown): { name: string; message: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  if (typeof error === 'string') return { name: 'Error', message: error };
  try {
    return { name: 'NonError', message: JSON.stringify(error) };
  } catch {
    return { name: 'NonError', message: String(error) };
  }
}
